import { useCallback } from "react";
import {
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from "nuqs";

/**
 * URL state for the appointment upsert modal.
 *
 * Param names are prefixed (`upsert-appointment-*`) rather than bare (`appointmentId`) so a page
 * can host this modal next to an appointment *filter* or a task upsert modal without the keys
 * colliding. `features/task-upsert` mirrors this file with `upsert-task-*`.
 *
 * `upsert-appointment-work-order-id` only matters on pages that have no work-order context of
 * their own (the calendar). `WorkOrderDetailPage` passes the id as a prop instead.
 */

const MODES = ["create", "edit"] as const;

export const useUpsertAppointmentSearchParams = () =>
  useQueryStates(
    {
      "upsert-appointment": parseAsStringLiteral(MODES),
      "upsert-appointment-id": parseAsInteger,
      "upsert-appointment-work-order-id": parseAsString,
    },
    { history: "replace", shallow: true, clearOnDefault: true },
  );

export type UpsertAppointmentParams = ReturnType<
  typeof useUpsertAppointmentSearchParams
>[0];

export function useUpsertAppointment() {
  const [params, setParams] = useUpsertAppointmentSearchParams();

  const openCreate = useCallback(
    (workOrderId?: string) =>
      setParams({
        "upsert-appointment": "create",
        "upsert-appointment-id": null,
        "upsert-appointment-work-order-id": workOrderId ?? null,
      }),
    [setParams],
  );

  const openEdit = useCallback(
    (id: number) =>
      setParams({
        "upsert-appointment": "edit",
        "upsert-appointment-id": id,
      }),
    [setParams],
  );

  const close = useCallback(
    () =>
      setParams({
        "upsert-appointment": null,
        "upsert-appointment-id": null,
        "upsert-appointment-work-order-id": null,
      }),
    [setParams],
  );

  return {
    /** Derived from the mode's presence — never a separate boolean param. */
    isOpen: params["upsert-appointment"] != null,
    mode: params["upsert-appointment"],
    appointmentId: params["upsert-appointment-id"],
    workOrderId: params["upsert-appointment-work-order-id"],
    openCreate,
    openEdit,
    close,
  };
}
